'use client';

import { c as _c } from "react/compiler-runtime";
import type { WikiPageImportance } from '@/generated/prisma/enums';
import { ArrowLeft, Search } from 'lucide-react';
import type { Route } from 'next';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { decodeHtmlEntities } from '@/lib/html-decode';
import { cn } from '@/lib/utils';
import { buildWikiPath } from '@/utils/wiki-paths';
type WikiPageData = {
  pageRef: string;
  title: string;
  importance: WikiPageImportance;
  filePaths: string[];
  parentSection?: string | null;
  children?: string[];
  relatedPages?: string[];
  content?: string;
};
type WikiSection = {
  id: string;
  title: string;
  pages: string[];
  subsections?: WikiSection[];
};
type WikiStructure = {
  sections?: WikiSection[];
};
type WikiTreeSidebarProps = {
  pages: WikiPageData[];
  selectedPageRef: string | null;
  onPageSelect?: (pageRef: string) => void;
  wikiStructure?: WikiStructure;
  isPublicView?: boolean;
  owner?: string;
  repo?: string;
  /** Wiki-level identity/status/actions, rendered between the back link and the tree. */
  header?: ReactNode;
};
type TreeNode = {
  id: string;
  title: string;
  type: 'section' | 'subsection' | 'page';
  children: TreeNode[];
  pageRef?: string;
  page?: WikiPageData;
  importance?: WikiPageImportance;
};
type WikiPageLinkProps = {
  href: Route;
  pageRef: string;
  onPageSelect?: (pageRef: string) => void;
  onClick?: () => void;
  className: string;
  children: ReactNode;
};

// Every page of the wiki is already loaded, so a plain click switches pages through
// onPageSelect instead of a router navigation that would refetch it from the server.
// The href stays for new tabs and crawlers.
function WikiPageLink(t0) {
  const $ = _c(9);
  const {
    href,
    pageRef,
    onPageSelect,
    onClick,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== onPageSelect || $[1] !== pageRef) {
    t1 = event => {
      if (onPageSelect) {
        event.preventDefault();
        onPageSelect(pageRef);
      }
    };
    $[0] = onPageSelect;
    $[1] = pageRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== className || $[5] !== href || $[6] !== onClick || $[7] !== t1) {
    t2 = <Link href={href} prefetch={false} onClick={onClick} onNavigate={t1} className={className}>{children}</Link>;
    $[3] = children;
    $[4] = className;
    $[5] = href;
    $[6] = onClick;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
export function WikiTreeSidebar(t0) {
  const $ = _c(22);
  const {
    pages,
    selectedPageRef,
    onPageSelect,
    wikiStructure,
    isPublicView: t1,
    owner,
    repo,
    header
  } = t0;
  const isPublicView = t1 === undefined ? false : t1;
  const [searchQuery, setSearchQuery] = useState("");
  const params = useParams();
  const versionPathParam = params?.version as string | undefined;
  let t2;
  bb0: {
    if (!pages || pages.length === 0) {
      let t3;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[0] = t3;
      } else {
        t3 = $[0];
      }
      t2 = t3;
      break bb0;
    }
    if (!wikiStructure || !wikiStructure.sections) {
      let t3;
      if ($[1] !== pages) {
        t3 = pages.map(_temp);
        $[1] = pages;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t2 = t3;
      break bb0;
    }
    let tree;
    if ($[3] !== pages || $[4] !== wikiStructure.sections) {
      tree = [];
      wikiStructure.sections.forEach(section => {
        const sectionNode = {
          id: section.id || section.title,
          title: decodeHtmlEntities(section.title),
          type: "section",
          children: []
        };
        if (section.pages && Array.isArray(section.pages)) {
          section.pages.forEach(pageId => {
            const page_0 = pages.find(p => p.pageRef === pageId);
            if (page_0) {
              sectionNode.children.push({
                id: page_0.pageRef || `page-${pageId}`,
                title: decodeHtmlEntities(page_0.title),
                type: "page",
                children: [],
                pageRef: page_0.pageRef,
                page: page_0,
                importance: page_0.importance
              });
            }
          });
        }
        sectionNode.children.sort(_temp2);
        if (sectionNode.children.length > 0) {
          tree.push(sectionNode);
        }
      });
      $[3] = pages;
      $[4] = wikiStructure.sections;
      $[5] = tree;
    } else {
      tree = $[5];
    }
    t2 = tree;
  }
  const treeStructure = t2;
  let t3;
  if ($[6] !== header || $[7] !== isPublicView || $[8] !== onPageSelect || $[9] !== owner || $[10] !== pages || $[11] !== repo || $[12] !== searchQuery || $[13] !== selectedPageRef || $[14] !== treeStructure || $[15] !== versionPathParam) {
    const renderTreeNode = (node, t4) => {
      const depth = t4 === undefined ? 0 : t4;
      const isSelected = node.pageRef === selectedPageRef;
      if (node.type === "section" || node.type === "subsection") {
        const hasChildren = node.children.length > 0;
        return <div key={node.id} className={cn("mb-2", depth === 0 && "mb-6")}><div className={cn("w-full flex items-center px-3 py-1 text-sm", node.type === "section" && depth === 0 ? "font-semibold text-foreground text-base mb-1" : "font-medium text-muted-foreground")}><span className="line-clamp-2 text-left flex-1">{node.title}</span></div>{hasChildren && <div className="mt-1">{node.children.map(child => renderTreeNode(child, depth + 1))}</div>}</div>;
      }
      const pageUrl = owner && repo && node.pageRef ? buildWikiPath({
        owner,
        repo,
        version: versionPathParam,
        page: node.pageRef,
        isPublicView
      }) : "#";
      if (owner && repo && node.pageRef) {
        return <WikiPageLink key={node.id} href={pageUrl} pageRef={node.pageRef} onPageSelect={onPageSelect} className={cn("relative w-full flex items-center justify-start px-3 py-1.5 my-0.5 rounded-md text-sm transition-colors duration-200 ease", "cursor-pointer hover:text-foreground hover:bg-zinc-900", isSelected ? "text-foreground font-medium bg-zinc-900" : "text-muted-foreground")}><span className="text-left flex-1 line-clamp-2">{node.title}</span></WikiPageLink>;
      }
      return <button key={node.id} type="button" onClick={() => {
        if (node.pageRef && onPageSelect) {
          onPageSelect(node.pageRef);
        }
      }} disabled={!onPageSelect} className={cn("relative w-full flex items-center justify-start px-3 py-1.5 my-0.5 rounded-md text-sm transition-colors duration-200 ease", onPageSelect ? "cursor-pointer hover:text-foreground hover:bg-zinc-900" : "cursor-not-allowed opacity-50", isSelected ? "text-foreground font-medium bg-zinc-900" : "text-muted-foreground")}><span className="text-left flex-1 line-clamp-2">{node.title}</span></button>;
    };
    const filteredPages = searchQuery ? pages.filter(page_1 => decodeHtmlEntities(page_1.title).toLowerCase().includes(searchQuery.toLowerCase()) || page_1.filePaths?.some(path => path.toLowerCase().includes(searchQuery.toLowerCase()))) : null;
    let t5;
    if ($[17] !== isPublicView) {
      t5 = isPublicView ? <Button variant="ghost" size="sm" asChild={true} className="w-full justify-start text-sm text-muted-foreground hover:text-foreground hover:bg-zinc-900 -ml-2"><Link href="/wikis"><ArrowLeft className="mr-2 h-4 w-4" />Back to directory</Link></Button> : <Button variant="ghost" size="sm" asChild={true} className="w-full justify-start text-sm text-muted-foreground hover:text-foreground hover:bg-zinc-900 -ml-2"><Link href="/wiki"><ArrowLeft className="mr-2 h-4 w-4" />All repositories</Link></Button>;
      $[17] = isPublicView;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== pages.length || $[20] !== searchQuery) {
      t6 = pages.length > 0 && <div className="relative w-full"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input type="text" placeholder="Search pages..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 h-9 text-sm w-full bg-background hover:bg-zinc-900 transition-colors" /></div>;
      $[19] = pages.length;
      $[20] = searchQuery;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    t3 = <div className="py-6 space-y-4">{t5}{header}{t6}{searchQuery && filteredPages ? <div className="space-y-1 mt-2">{filteredPages.length > 0 ? filteredPages.map(page_2 => {
          if (owner && repo && page_2.pageRef) {
            const url = buildWikiPath({
              owner,
              repo,
              version: versionPathParam,
              page: page_2.pageRef,
              isPublicView
            });
            return <WikiPageLink key={page_2.pageRef} href={url} pageRef={page_2.pageRef} onPageSelect={onPageSelect} onClick={() => setSearchQuery("")} className={cn("relative w-full flex items-center justify-start px-2 py-2 my-0.5 rounded-md text-sm transition-colors duration-200 ease", "cursor-pointer hover:text-foreground hover:bg-zinc-900", selectedPageRef === page_2.pageRef ? "text-foreground font-medium bg-zinc-900" : "text-muted-foreground")}><span className="text-left flex-1 line-clamp-2">{decodeHtmlEntities(page_2.title)}</span></WikiPageLink>;
          }
          return <div key={page_2.pageRef} className="relative w-full flex items-center justify-start px-2 py-2 my-0.5 rounded-md text-sm opacity-50 cursor-not-allowed text-muted-foreground"><span className="text-left flex-1 line-clamp-2">{decodeHtmlEntities(page_2.title)}</span></div>;
        }) : <p className="text-sm text-muted-foreground text-center py-4">No pages found</p>}</div> : <div className="mt-2">{treeStructure.length > 0 ? treeStructure.map(node_0 => renderTreeNode(node_0)) : pages.map((page_3, index_0) => {
          if (owner && repo && page_3.pageRef) {
            const url_0 = buildWikiPath({
              owner,
              repo,
              version: versionPathParam,
              page: page_3.pageRef,
              isPublicView
            });
            return <WikiPageLink key={page_3.pageRef} href={url_0} pageRef={page_3.pageRef} onPageSelect={onPageSelect} className={cn("relative w-full flex items-center justify-start px-3 py-1.5 my-0.5 rounded-md text-sm transition-colors duration-200 ease", "cursor-pointer hover:text-foreground hover:bg-zinc-900", selectedPageRef === page_3.pageRef ? "text-foreground font-medium bg-zinc-900" : "text-muted-foreground")}><span className="text-left flex-1 line-clamp-2">{decodeHtmlEntities(page_3.title)}</span></WikiPageLink>;
          }
          return <div key={page_3.pageRef || `page-${index_0}`} className="relative w-full flex items-center justify-start px-3 py-1.5 my-0.5 rounded-md text-sm opacity-50 cursor-not-allowed text-muted-foreground"><span className="text-left flex-1 line-clamp-2">{decodeHtmlEntities(page_3.title)}</span></div>;
        })}</div>}</div>;
    $[6] = header;
    $[7] = isPublicView;
    $[8] = onPageSelect;
    $[9] = owner;
    $[10] = pages;
    $[11] = repo;
    $[12] = searchQuery;
    $[13] = selectedPageRef;
    $[14] = treeStructure;
    $[15] = versionPathParam;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
function _temp2(a, b) {
  if (!a.importance || !b.importance) {
    return 0;
  }
  const importanceWeights = {
    high: 3,
    medium: 2,
    low: 1
  };
  return (importanceWeights[b.importance] || 0) - (importanceWeights[a.importance] || 0);
}
function _temp(page, index) {
  return {
    id: page.pageRef || `page-${index}`,
    title: decodeHtmlEntities(page.title),
    type: "page" as const,
    children: [],
    pageRef: page.pageRef,
    page,
    importance: page.importance
  };
}